import { useEffect, useRef, useState } from "react";
import { TextGroup } from "@/types/layerslider";
import { getEasing, getTransform } from "@/main/_components/utils/slider-animations";

interface SliderTextProps {
  group: TextGroup;
  lang: string;
}

const SliderText: React.FC<SliderTextProps> = ({ group, lang }) => {
  const [isInView, setIsInView] = useState<boolean>(false);
  const textRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const text = textRef.current;
    if (!text) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
      },
      { threshold: 0.1 },
    );

    observer.observe(text);

    return () => {
      observer.unobserve(text);
    };
  }, []);

  const easingIn = getEasing(group.easingin);
  const easingOut = getEasing(group.easingout);
  const transitionInTransform = getTransform(group.transitionin, easingIn);
  const transitionOutTransform = getTransform(group.transitionout, easingOut);
  const [windowWidth, setWindowWidth] = useState<number>(1200);

  useEffect(() => {
    if (typeof window !== "undefined") {
      setWindowWidth(window.innerWidth);

      const handleResize = () => setWindowWidth(window.innerWidth);
      window.addEventListener("resize", handleResize);

      return () => window.removeEventListener("resize", handleResize);
    }
  }, []);
  const scaleFactor = windowWidth / 1200;
  const responsiveFontSize = `${Math.min(parseFloat(group.fontsize), parseFloat(group.fontsize) * scaleFactor)}px`;
  const responsiveLeft = `${Math.min(parseFloat(group.left[lang]) * scaleFactor, parseFloat(group.left[lang]))}px`;
  const responsiveTop = `${Math.min(parseFloat(group.top[lang]) * scaleFactor, parseFloat(group.top[lang]))}px`;
  const responsivePadding = `${parseFloat(group.padding) * scaleFactor}px`;
  const responsiveRadius = `${parseFloat(group.radius) * scaleFactor}px`;

  return (
    <div
      ref={textRef}
      className="absolute transition-all"
      style={{
        left: responsiveLeft,
        top: responsiveTop,
        fontSize: responsiveFontSize,
        padding: responsivePadding,
        borderRadius: responsiveRadius,
        fontWeight: group.fontweight,
        color: group.color,
        backgroundColor: group.bg,
        fontFamily: group.font,
        zIndex: group.p_index,
        order: group.sort_order,
        opacity: isInView ? 1 : 0,
        transform: isInView ? transitionInTransform : transitionOutTransform,
        transition: isInView
          ? `opacity ${group.durationin}ms ${easingIn}, transform ${group.durationin}ms ${easingIn}`
          : `opacity ${group.durationout}ms ${easingOut}, transform ${group.durationout}ms ${easingOut}`,
      }}
    >
      {group.description[lang]}
    </div>
  );
};

export default SliderText;
